import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, X, ClipboardList, Filter } from "lucide-react";

const ACTION_BADGE = {
  "project.create": { color: "#004D99", label: "yarat" },
  "segment.create": { color: "#004D99", label: "yarat" },
  "stage.create": { color: "#004D99", label: "yarat" },
  "inspection.create": { color: "#004D99", label: "yarat" },
  "user.create": { color: "#004D99", label: "yarat" },
  "user.update": { color: "#F59E0B", label: "redaktə" },
  "user.delete": { color: "#EF4444", label: "sil" },
  "inspection.delete": { color: "#EF4444", label: "sil" },
  "stage.delete": { color: "#EF4444", label: "sil" },
};

const TARGET_BADGE = {
  project: "Layihə",
  segment: "Hissə",
  stage: "Mərhələ",
  inspection: "Yoxlanış",
  user: "İstifadəçi",
  norm: "Norma",
};

export default function AdminAudit() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [query, setQuery] = useState("");
  const [targetType, setTargetType] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (targetType !== "all") params.target_type = targetType;
    api
      .get("/audit-logs", { params })
      .then((r) => setLogs(r.data))
      .finally(() => setLoading(false));
  }, [targetType]);

  if (user?.role !== "superadmin") {
    return (
      <div className="text-slate-500">Yalnız Super Admin bu səhifəyə daxil ola bilər.</div>
    );
  }

  const q = query.trim().toLowerCase();
  const filtered = logs.filter((l) => {
    if (!q) return true;
    return (
      (l.actor_name || "").toLowerCase().includes(q) ||
      (l.action || "").toLowerCase().includes(q) ||
      (l.actor_role || "").toLowerCase().includes(q) ||
      (l.target_id || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div>
        <div className="label-overline mb-1">audit log</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">
          Sistem Audit Jurnalı
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          Bütün yaradılma, redaktə və silmə əməliyyatları
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Aktor, rol, action və ya target id..."
            className="pl-9 pr-9 bg-white"
            data-testid="audit-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={targetType} onValueChange={setTargetType}>
          <SelectTrigger className="w-[200px] bg-white" data-testid="audit-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Bütün target tipləri</SelectItem>
            {Object.entries(TARGET_BADGE).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="mono text-xs text-slate-500">
          {filtered.length} / {logs.length}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 mono">Yüklənir...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <ClipboardList className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            Log yoxdur
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 mono">
              <tr>
                <th className="text-left px-4 py-3">Vaxt</th>
                <th className="text-left px-4 py-3">Aktor</th>
                <th className="text-left px-4 py-3">Rol</th>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Target</th>
                <th className="text-left px-4 py-3">Detallar</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const badge = ACTION_BADGE[l.action] || { color: "#94A3B8", label: l.action };
                return (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 mono text-xs text-slate-500 whitespace-nowrap">
                      {(l.created_at || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="px-4 py-2 font-medium">{l.actor_name || "—"}</td>
                    <td className="px-4 py-2 mono text-[10px] text-slate-500">
                      {l.actor_role}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] mono uppercase tracking-wide text-white"
                        style={{ background: badge.color }}
                      >
                        {l.action}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-xs">
                        {TARGET_BADGE[l.target_type] || l.target_type}
                      </span>
                      <div className="mono text-[10px] text-slate-400">
                        {(l.target_id || "").slice(0, 8)}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 max-w-md">
                      <code className="text-[10px] whitespace-pre-wrap break-all">
                        {Object.entries(l.meta || {})
                          .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
                          .join(" · ") || "—"}
                      </code>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
